import { action, computed, makeObservable, observable, values } from 'mobx';

import { ProviderUid } from '@gem/call/CallProviders/callProviders.types';
import { ICallMember } from '@gem/call/Model/callModel.types';
import { TAvatarBackgroundColorIndex } from '@gem/call/Removable/components/Avatar/Avatar';

import { Call } from '../Call';
import { MemberDevice } from './MemberDevice';

export class Member {
  call: Call;
  userId: string;
  displayName = '';
  avatarUrl: string | undefined = undefined;
  @observable isAdmin = false;
  devices = observable.map<ProviderUid, MemberDevice>({});
  defaultDevice: MemberDevice;

  constructor(call: Call, data: ICallMember) {
    makeObservable(this);
    this.call = call;
    this.userId = data.userId;
    this.displayName = data.username;
    this.avatarUrl = data.avatarUrl;
    this.isAdmin = !!data.isAdmin;
    this.defaultDevice = new MemberDevice({
      call: this.call,
      member: this,
      isDefault: true,
      device: null,
      state: null,
      isHandRaised: false,
      handRaisedAt: null,
    });

    if (data.device) {
      this.devices.set(
        data.device.deviceId,
        new MemberDevice({
          device: data.device,
          state: data.status,
          isHandRaised: data.isHandRaised,
          handRaisedAt: data.handRaisedAt,
          call: this.call,
          isDefault: false,
          member: this,
        })
      );
    }
  }

  @computed
  get avatarColorIndex(): TAvatarBackgroundColorIndex {
    const index = this.userId ? Number(this.userId[this.userId.length - 1]) : 0;

    return String(index) as TAvatarBackgroundColorIndex;
  }

  @computed
  get isMe(): boolean {
    return this.call.meStore.id === this.userId;
  }

  @computed
  get isInitiator(): boolean {
    return this.call.initiatorId === this.userId;
  }

  @computed
  get devicesOnCallList(): MemberDevice[] {
    return values(this.devices).filter((item) => item.isOnCallByProvider);
  }

  @computed
  get devicesList(): MemberDevice[] {
    const devicesList = this.devicesOnCallList;
    if (devicesList.length === 0) {
      return [this.defaultDevice];
    } else {
      return devicesList;
    }
  }

  getDevice(deviceId: ProviderUid): MemberDevice | null {
    return this.devices.get(deviceId) || null;
  }

  getDeviceByUid(uid: ProviderUid): MemberDevice | null {
    return this.devicesList.find((device) => device.uid === uid) || null;
  }

  getDeviceByScreenUid(uid: ProviderUid): MemberDevice | null {
    return this.devicesList.find((device) => device.shareScreenUid === uid) || null;
  }

  setDevice(data: ICallMember): void {
    if (data.device && !this.devices.get(data.device.deviceId)) {
      this.devices.set(
        data.device.deviceId,
        new MemberDevice({
          device: data.device,
          state: data.status,
          isHandRaised: data.isHandRaised,
          handRaisedAt: data.handRaisedAt,
          call: this.call,
          member: this,
          isDefault: false,
        })
      );
    }
  }

  @action
  deleteDevice(uid: ProviderUid): void {
    const device = this.devices.get(uid);

    if (device) {
      this.defaultDevice.setState(device.providerState);
      this.devices.delete(uid);
    }
  }

  @action setIsAdmin(value: boolean): void {
    this.isAdmin = value;
  }
}
